import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import CartDrawer from './CartDrawer';

const NAV_ITEMS = [
  { type: 'route', to: '/', label: 'ANASAYFA' },
  { type: 'section', id: 'services', label: 'HİZMETLER' },
  { type: 'route', to: '/urunler', label: 'ÜRÜNLER' },
  { type: 'section', id: 'about', label: 'HAKKIMIZDA' },
  { type: 'section', id: 'contact', label: 'İLETİŞİM' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { cartCount } = useCart();
  const lastY = useRef(0);

  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, 'change', (current) => {
    const previous = lastY.current;
    setScrolled(current > 40);
    if (current > previous && current > 120) {
      setHidden(true);
    } else {
      setHidden(false);
    }
    lastY.current = current;
  });

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const goToSection = (id) => {
    setMobileOpen(false);
    if (location.pathname === '/') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: id } });
    }
  };

  const goHome = () => {
    setMobileOpen(false);
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <>
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      <motion.header
        animate={{ y: hidden ? '-100%' : '0%' }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? 'backdrop-blur-xl bg-black/60 border-b border-border-soft' : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-20 sm:h-28 lg:h-44 flex items-center justify-between">
          {/* Logo */}
          <button onClick={goHome} className="group flex items-center">
            <img
              src="/logo.png"
              alt="Öncü Boya"
              className="h-16 sm:h-24 lg:h-40 w-auto object-contain group-hover:scale-105 transition-transform duration-500"
            />
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {NAV_ITEMS.map((item) => (
              item.type === 'route' ? (
                <Link
                  key={item.label}
                  to={item.to}
                  className="text-sm font-medium tracking-widest text-text-muted hover:text-white transition-colors duration-300"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => goToSection(item.id)}
                  className="text-sm font-medium tracking-widest text-text-muted hover:text-white transition-colors duration-300"
                >
                  {item.label}
                </button>
              )
            ))}
          </nav>

          {/* CTA + Sepet */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => goToSection('contact')}
              className="hidden lg:inline-flex items-center px-6 py-2.5 rounded-full border border-accent-gold text-accent-gold text-sm font-bold tracking-widest hover:bg-accent-gold hover:text-black transition-all duration-500"
            >
              RANDEVU AL
            </button>

            {/* Sepet ikonu */}
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 text-white/70 hover:text-white transition"
              aria-label="Sepet"
            >
              <ShoppingCart size={24} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-accent-red text-white text-xs font-bold flex items-center justify-center">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </button>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden text-white p-2"
              aria-label="Menüyü aç"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile full-screen overlay menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[60] bg-bg-primary/98 backdrop-blur-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-20">
              <span className="text-white font-bold tracking-tight text-lg">
                ÖNCÜ <span className="text-accent-red">BOYA</span>
              </span>
              <button onClick={() => setMobileOpen(false)} className="text-white p-2" aria-label="Menüyü kapat">
                <X size={28} />
              </button>
            </div>

            <nav className="flex-1 flex flex-col items-center justify-center gap-8">
              {NAV_ITEMS.map((item, idx) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.08 * idx, ease: [0.22, 1, 0.36, 1] }}
                >
                  {item.type === 'route' ? (
                    <Link
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className="text-4xl font-black tracking-tight text-white hover:text-accent-gold transition-colors duration-300"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => goToSection(item.id)}
                      className="text-4xl font-black tracking-tight text-white hover:text-accent-gold transition-colors duration-300"
                    >
                      {item.label}
                    </button>
                  )}
                </motion.div>
              ))}

              <motion.button
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.08 * NAV_ITEMS.length, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => goToSection('contact')}
                className="mt-4 px-10 py-4 rounded-full border border-accent-gold text-accent-gold font-bold tracking-widest hover:bg-accent-gold hover:text-black transition-all duration-500"
              >
                RANDEVU AL
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
